import React, { useState, useEffect, useMemo } from 'react'

import {
  BoxContainer,
  Button,
  Typography
} from '../../../../../../../components'

import Trash from '../../../../../../../assets/Icons/Trash'
import ecbrApi from '../../../../../../../services/ecbrApi'
import { useInfinitePagination } from '../../../../../../../hooks/useInfinitePagination'
import { UserEvents } from '../constants'
import { errorResponseStatusHandler } from '../../../../../../../utils/handlers/errorResponseStatusHandler'
import { ecbrUserRoleListForSelect } from '../../../../../../../constants/roles'
import ErrorMessage from '../../../../../../../components/Form/ErrorMessage'
import { getDate } from '../../../../../../../utils/dateUtils'
import useUrlParam from '../../../../../../../hooks/useUrlParam'
import { useQuery } from 'react-query'
import {
  adminLoadCertificateOptions,
  adminLoadPillarOptions
} from '../../../../../../../helpers/loadOptions'
import { Content } from '../../../../../../../components/Content'

const pagination = { page: 1, take: 5 }

const PendingInvites = ({ eventListener }) => {
  const [companyId, _brandId] = useUrlParam(
    '/admin/companies/:id/brands/:id/details'
  )
  const [error, setError] = useState('')
  const [searchParameters, _setSearchParameters] = useState({
    ...pagination
  })

  const {
    data: pendingInvites,
    fetchNextPage,
    hasNextPage,
    isLastPage,
    isFetching,
    isLoading,
    refetch
  } = useInfinitePagination(
    'pendingInvites',
    async (params) => {
      const { pageParam = searchParameters.page } = params
      return ecbrApi.userInvites.getPendingInvites({
        ...searchParameters,
        page: pageParam || searchParameters.page,
        companyId: Number(companyId)
      })
    },
    {
      cacheTime: 0,
      onSuccess: () => {
        setError('')
      },
      onError: () => {
        setError('Não foi possível carregar os dados')
      }
    }
  )

  const filteredInvites = useMemo(
    () =>
      pendingInvites?.filter((invite) =>
        ecbrUserRoleListForSelect.some((role) => role.value === invite.role)
      ),
    [pendingInvites]
  )

  useEffect(() => {
    eventListener.subscribe(UserEvents.INVITE_SENT, refetch)
    eventListener.subscribe(UserEvents.INVITE_DELETED, refetch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: certificateOptions } = useQuery('certificateOptions', () =>
    adminLoadCertificateOptions()('')
  )

  const { data: pillarOptions } = useQuery('pillarOptions', () =>
    adminLoadPillarOptions()('')
  )

  const handleReinvite = async (invite) => {
    try {
      setError('')
      await ecbrApi.userInvites.reinvite(invite.id)
    } catch (err) {
      errorResponseStatusHandler(err, setError)
    }

    eventListener?.publish(UserEvents.INVITE_SENT)
  }

  const handleDelete = async (invite) => {
    try {
      setError('')
      await ecbrApi.userInvites.delete(invite.id)
    } catch (err) {
      errorResponseStatusHandler(err, setError)
    }

    eventListener?.publish(UserEvents.INVITE_DELETED)
  }

  return (
    <BoxContainer>
      <Typography type="sectionTitle">Convites enviados</Typography>
      <Content templateColumns="13% 16% 14% 9% 9% 9% 6% 10%">
        <header>
          <ul>
            <li>Nome do usuário</li>
            <li>E-mail de acesso</li>
            <li>Perfil</li>
            <li>Certificado</li>
            <li>Pilares</li>
            <li>Data de envio</li>
            <li>Ações</li>
            <li>&nbsp;</li>
          </ul>
        </header>
        <main>
          {filteredInvites?.map?.((invite) => (
            <ul key={invite.id}>
              <li>{invite.name}</li>
              <li>{invite.email}</li>
              <li>
                {ecbrUserRoleListForSelect.find(
                  (role) => role.value === invite.role
                )?.label || '-'}
              </li>
              <li>
                {certificateOptions?.find(
                  (cert) => cert.value === invite.certificateId
                )?.label || '-'}
              </li>
              <li>
                {pillarOptions
                  ?.filter((pillar) =>
                    invite.pillarIds
                      ?.split?.(',')
                      .includes(pillar.value.toString())
                  )
                  ?.map?.((pillar) => pillar.label)
                  ?.join?.(', ') || '-'}
              </li>
              <li>{getDate(invite.createdAt)}</li>
              <li>
                <div onClick={() => handleDelete(invite)}>
                  <Trash color="#858585" />
                </div>
              </li>
              <li>
                <Button
                  type="button"
                  variant="outlinedLink"
                  text="Reenviar convite"
                  onClick={() => handleReinvite(invite)}
                />
              </li>
            </ul>
          ))}
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {hasNextPage && !isLastPage && (
            <Button
              type="button"
              variant="outlined"
              text={isLoading || isFetching ? 'Carregando...' : 'Ver mais'}
              width="120px"
              onClick={() => fetchNextPage()}
            />
          )}
        </main>
      </Content>
    </BoxContainer>
  )
}

export default PendingInvites
